import { useCallback, useEffect, useState } from "react";

// Tiny localStorage-backed preference with cross-component sync (same tab via a
// custom event, other tabs via the native storage event). Mirrors the pattern
// already used for theme. No new settings architecture.
const EVT = "chronicles:localpref";

function read(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return fallback;
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

export function useLocalPref(key, fallback) {
  const [value, setValue] = useState(() => read(key, fallback));

  useEffect(() => {
    const onCustom = (e) => {
      if (e.detail?.key === key) setValue(read(key, fallback));
    };
    const onStorage = (e) => {
      if (e.key === key) setValue(read(key, fallback));
    };
    window.addEventListener(EVT, onCustom);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(EVT, onCustom);
      window.removeEventListener("storage", onStorage);
    };
  }, [key, fallback]);

  const set = useCallback(
    (next) => {
      try {
        localStorage.setItem(key, JSON.stringify(next));
      } catch {
        /* ignore quota / privacy-mode errors */
      }
      window.dispatchEvent(new CustomEvent(EVT, { detail: { key } }));
      setValue(next);
    },
    [key]
  );

  return [value, set];
}
